import './BetbookPosts.less';

import React, { useEffect, useLayoutEffect } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';

import { navigate } from '@/routing';
import i18n from 'nike-ui/src/common/i18n';
import EmptyState from 'nike-ui/src/components/empty-state/EmptyState';
import FloatingButton from 'nike-ui/src/components/floating-button/FloatingButton';
import InfiniteScroll from '@/responsive/components/InfiniteScroll/InfiniteScroll';
import LoaderMini from '@/ui/views/LoaderMini';
import { loadAuthorBetbookPosts, loadMoreAuthorBetbookPosts, setScroll } from '@/betbook/handlers';
import {
    hasNextPage$,
    initialBetbookPostsLoadingDone$,
    loadingBetbookPostsPending$,
    posts$,
    scrollPosition$
} from '@/betbook/subs';
import { accountVerificationStatus$, loggedIn$ } from '@/account/subs';

import BetbookFilters from '@/betbook/views/BetbookFilters';
import { NotVerifiedInfo } from '@/betbook/views/BetbookPosts';
import NotFoundPostsIcon from 'nike-ui/src/icons/NotFoundPostsIcon';
import { betbookNewRoute } from '@/betbook/utils';
import { BetboookPostsSortTypeOpions, POST_ANALYSIS } from 'nike-ui/src/components/betbook/constants';
import { useBetbookLastParam } from 'nike-ui/src/components/betbook/utils';
import BetbookAnalysisItem from '@/betbook/views/BetbookAnalysisItem';
import MultipleComponentsLoader from '@/common/components/loaders/MultipleComponentsLoader';
import BetbookPostItemLoader from '@/betbook/views/BetbookPostItemLoader';

interface BetbookPostsAuthorProps {
    sortType?: BetboookPostsSortTypeOpions;
    nickname: string;
}
function BetbookAnalyseAuthor({ nickname, sortType }: BetbookPostsAuthorProps) {
    const { subscribe } = useReframe();
    const [lastParam, setLastParam] = useBetbookLastParam();
    const scrollPosition = subscribe(scrollPosition$) || 0;
    const posts = subscribe(posts$);
    const initialLoadingDone = subscribe(initialBetbookPostsLoadingDone$);
    const loadingPending = subscribe(loadingBetbookPostsPending$);
    const hasNextPage = subscribe(hasNextPage$);
    const loggedIn = subscribe(loggedIn$);
    const verificationStatus = subscribe(accountVerificationStatus$);
    const isVerified = verificationStatus === 'VERIFIED';
    const betbookViewPort = document.querySelector('.layout-main-col');
    const floatingBtnDisabled = !loggedIn || (loggedIn && !isVerified);

    useLayoutEffect(() => {
        if (betbookViewPort && window.matchMedia('(min-width: 800px)')) {
            betbookViewPort.scrollTo(0, scrollPosition);
        }
    }, [scrollPosition, betbookViewPort]);
    useEffect(() => {
        if (
            !lastParam ||
            lastParam?.nickname !== nickname ||
            lastParam?.postType !== POST_ANALYSIS ||
            lastParam?.sortType !== sortType
        ) {
            dispatch(loadAuthorBetbookPosts(nickname, POST_ANALYSIS, 0, sortType));
            setLastParam({ nickname, postType: POST_ANALYSIS, sortType });
        }
    }, [lastParam, nickname, setLastParam, sortType]);

    function handlePositionMemoize() {
        betbookViewPort && dispatch(setScroll(betbookViewPort?.scrollTop));
    }

    function handleNewAnalysis() {
        if (loggedIn) {
            navigate(betbookNewRoute(POST_ANALYSIS));
        } else {
            dispatch(['login.openScreen']);
        }
    }

    function handleLoadMore() {
        dispatch(loadMoreAuthorBetbookPosts(POST_ANALYSIS));
    }

    return (
        <div onClick={handlePositionMemoize}>
            {loggedIn && !isVerified && <NotVerifiedInfo verificationStatus={verificationStatus} />}
            <BetbookFilters postType={POST_ANALYSIS} />
            {initialLoadingDone ? (
                posts?.length ? (
                    <InfiniteScroll
                        className="betbook-posts"
                        hasMore={hasNextPage && !loadingPending}
                        loadMore={handleLoadMore}
                        loader={<LoaderMini className="infinite-loader betbook-more-posts-loader" key="loader" />}
                        threshold={0}
                        initialLoad={false}
                    >
                        {posts.map(post => (
                            <BetbookAnalysisItem key={post.id} post={post} />
                        ))}
                        <FloatingButton
                            onClick={handleNewAnalysis}
                            dataAtid="betbook-new-post"
                            disabled={floatingBtnDisabled}
                        />
                    </InfiniteScroll>
                ) : (
                    <div className="betbook-posts">
                        <EmptyState
                            text={i18n('betbook.author.posts.empty')}
                            textAboveIcon
                            stateIcon={<NotFoundPostsIcon />}
                        />
                        <FloatingButton
                            onClick={handleNewAnalysis}
                            dataAtid="betbook-new-post"
                            disabled={floatingBtnDisabled}
                        />
                    </div>
                )
            ) : (
                <div className="betbook-posts">
                    <MultipleComponentsLoader itemsCount={2} componentLoader={BetbookPostItemLoader} />
                </div>
            )}
        </div>
    );
}

export default BetbookAnalyseAuthor;
